import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Printer, Save, TestTube } from "lucide-react";
import { usePrintTemplates, PrintTemplate, TemplateType, TEMPLATE_TYPE_LABELS, DEFAULT_TEMPLATE } from "@/hooks/usePrintTemplates";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { printTestReceipt } from "@/lib/print-template-test";
import { ReceiptPreview } from "./ReceiptPreview";
import { toast } from "sonner";

export function PrintTemplateSettings() {
  const { activeRestaurant } = useRestaurantContext();
  const { templates, isLoading, getTemplate, saveTemplate } = usePrintTemplates();
  const [activeTab, setActiveTab] = useState<TemplateType>("order_receipt");
  const [currentTemplate, setCurrentTemplate] = useState<PrintTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  // Update current template when tab changes or templates load
  useEffect(() => {
    if (!isLoading && activeRestaurant?.id) {
      setCurrentTemplate(getTemplate(activeTab));
    }
  }, [activeTab, templates, isLoading, getTemplate, activeRestaurant?.id]);

  const handleChange = <K extends keyof PrintTemplate>(field: K, value: PrintTemplate[K]) => {
    if (!currentTemplate) return;
    setCurrentTemplate(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleSave = async () => {
    if (!currentTemplate) return;
    setIsSaving(true);
    await saveTemplate(currentTemplate);
    setIsSaving(false);
  };

  const handleTest = async () => {
    if (!currentTemplate || !activeRestaurant?.id) return;
    setIsTesting(true);
    try {
      await printTestReceipt(activeRestaurant.id, currentTemplate);
      toast.success("Teste de impressão enviado!");
    } catch (error) {
      console.error("Error testing print:", error);
      toast.error("Erro ao enviar teste de impressão");
    } finally {
      setIsTesting(false);
    }
  };

  const handleReset = () => {
    if (!activeRestaurant?.id) return;
    setCurrentTemplate({
      ...DEFAULT_TEMPLATE,
      id: currentTemplate?.id,
      restaurant_id: activeRestaurant.id,
      template_type: activeTab,
    });
  };

  if (isLoading || !currentTemplate) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Templates de Impressão
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-muted rounded" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Printer className="h-5 w-5" />
          Templates de Impressão
        </CardTitle>
        <CardDescription>
          Configure o formato e estilo dos recibos impressos
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TemplateType)}>
          <TabsList className="grid grid-cols-2 lg:grid-cols-6 w-full">
            {(Object.keys(TEMPLATE_TYPE_LABELS) as TemplateType[]).map((type) => (
              <TabsTrigger key={type} value={type} className="text-xs sm:text-sm">
                {TEMPLATE_TYPE_LABELS[type]}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Settings Column */}
              <div className="space-y-6">
                {/* Title/Header Settings */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Título / Cabeçalho
                  </h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="title-bold">Negrito</Label>
                      <Switch
                        id="title-bold"
                        checked={currentTemplate.title_bold}
                        onCheckedChange={(v) => handleChange("title_bold", v)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="title-size">Tamanho</Label>
                      <Select
                        value={currentTemplate.title_size}
                        onValueChange={(v) => handleChange("title_size", v as "normal" | "double")}
                      >
                        <SelectTrigger id="title-size">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="double">Grande (Duplo)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="header-text">Texto de Cabeçalho (opcional)</Label>
                    <Input
                      id="header-text"
                      placeholder="Ex: Bem-vindo ao Restaurante!"
                      value={currentTemplate.header_text || ""}
                      onChange={(e) => handleChange("header_text", e.target.value || null)}
                    />
                  </div>
                </div>

                {/* Items Settings */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Itens / Produtos
                  </h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="items-bold">Negrito</Label>
                      <Switch
                        id="items-bold"
                        checked={currentTemplate.items_bold}
                        onCheckedChange={(v) => handleChange("items_bold", v)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="items-size">Tamanho</Label>
                      <Select
                        value={currentTemplate.items_size}
                        onValueChange={(v) => handleChange("items_size", v as "normal" | "double")}
                      >
                        <SelectTrigger id="items-size">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="double">Grande (Duplo)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Totals Settings */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Totais / Valores
                  </h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="totals-bold">Negrito</Label>
                      <Switch
                        id="totals-bold"
                        checked={currentTemplate.totals_bold}
                        onCheckedChange={(v) => handleChange("totals_bold", v)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="totals-size">Tamanho</Label>
                      <Select
                        value={currentTemplate.totals_size}
                        onValueChange={(v) => handleChange("totals_size", v as "normal" | "double")}
                      >
                        <SelectTrigger id="totals-size">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="double">Grande (Duplo)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Footer Settings */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Rodapé
                  </h4>
                  <div className="space-y-2">
                    <Label htmlFor="footer-text">Mensagem de Rodapé</Label>
                    <Input
                      id="footer-text"
                      placeholder="Ex: Obrigado pela preferência!"
                      value={currentTemplate.footer_text || ""}
                      onChange={(e) => handleChange("footer_text", e.target.value || null)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="software-footer">Mostrar "Altus Food"</Label>
                      <p className="text-xs text-muted-foreground">Exibe o nome do software no rodapé</p>
                    </div>
                    <Switch
                      id="software-footer"
                      checked={currentTemplate.show_software_footer}
                      onCheckedChange={(v) => handleChange("show_software_footer", v)}
                    />
                  </div>
                </div>

                {/* Visual Settings */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Visual
                  </h4>
                  <div className="space-y-2">
                    <Label htmlFor="separator">Caractere Separador</Label>
                    <Select
                      value={currentTemplate.separator_char}
                      onValueChange={(v) => handleChange("separator_char", v)}
                    >
                      <SelectTrigger id="separator" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="=">= (Igual)</SelectItem>
                        <SelectItem value="-">- (Hífen)</SelectItem>
                        <SelectItem value="*">* (Asterisco)</SelectItem>
                        <SelectItem value="_">_ (Underscore)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <Button onClick={handleSave} disabled={isSaving} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Salvando..." : "Salvar Configurações"}
                  </Button>
                  <Button variant="outline" onClick={handleTest} disabled={isTesting}>
                    <TestTube className="h-4 w-4 mr-2" />
                    {isTesting ? "Enviando..." : "Imprimir Teste"}
                  </Button>
                  <Button variant="ghost" onClick={handleReset}>
                    Restaurar Padrão
                  </Button>
                </div>
              </div>

              {/* Preview Column */}
              <div className="lg:sticky lg:top-6 lg:self-start">
                <ReceiptPreview template={currentTemplate} />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
